import { EmployeeBase } from "../json-schemas/base/employee"
import { MarketplaceBase } from "../json-schemas/base/marketplace"
import { SpecificIncreaseBase } from "../json-schemas/base/specific_increase"
import { SpecificIncreaseRuleBase } from "../json-schemas/base/specific_increase_rule"
import { SpecificPriceBase } from "../json-schemas/base/specific_price"
import { SpecificPriceRuleBase } from "../json-schemas/base/specific_price_rule"
import { SupplierBase } from "../json-schemas/base/supplier"
import { CastingValidateLodgingIcalUrlResponse } from "../json-schemas/serializers/casting-validate-lodging-ical-url-response"
import { SupplierCastingsGetGroupSessionsAtDay } from "../json-schemas/serializers/supplier-castings-get-group-sessions-at-day"
import { SuppliersCastingsGetServiceResponse } from "../json-schemas/serializers/suppliers-castings-get-service-response"
import { SuppliersCastingsGetServicesResponse } from "../json-schemas/serializers/suppliers-castings-get-services-response"
import { WebsiteCastingWidgetShowResponse } from "../json-schemas/serializers/website-casting-widget-show-response"
import { httpDelete, httpGet, httpGetRoute, httpPatch, httpPost, httpPostMultipart, httpPostSync } from "../utils/http.service"
import { TypedID } from "../utils/iots.service"
import { moment } from "../utils/moment.service"
import { CastingType, WhyThisEmployeeIsMissingResponse } from "./castings.service"

export type CastingEmployeesMatching = { report: WhyThisEmployeeIsMissingResponse, employee: Pick<EmployeeBase, 'id' | 'name'> }[]

interface PricedEventRaw {
  tax_cents: number | string
  price_cents: number | string
  instances: {
    start_date: string
    end_date: string
    tax_cents: number | string
    price_cents: number | string
    matched_specific_pricing_method: Pick<SpecificPriceBase | SpecificIncreaseBase, 'id' | 'name' | 'class_name'>

    /** For price matching analysis as an operator (ex. in casting) */
    ordered_specific_pricing_modes_candidates?: ((Pick<SpecificPriceBase | SpecificIncreaseBase, 'id' | 'name'> & { specific_pricing_method_rules: Pick<SpecificPriceRuleBase | SpecificIncreaseRuleBase, 'id' | 'rule'>[] }))[]

    /** For price matching analysis as an operator (ex. in casting) */
    debug_checked_specific_pricing_method_rules?: { rule_id: SpecificPriceRuleBase['id'] | SpecificIncreaseRuleBase['id'], matches: boolean }[]
  }[]
}

type ComputedCastingPricesForEventsRaw = {
  offered_service_combination_id: TypedID<'OfferedServiceCombination'>
  supplier_id: TypedID<'Supplier'>
  first_reccurrence: PricedEventRaw[]
  second_reccurrence: PricedEventRaw[]
}[]

type ComputedCastingTotalPricesRaw = {
  supplier_id: TypedID<'Supplier'>
  total_price_cents: number
  total_tax_cents: number
}[]

type RealtimeAvailabilities = {
  start_time: string
  end_time: string
}[][]

export type MarketPolymorphicId = Pick<SupplierBase | MarketplaceBase, 'id' | 'market_type'>

export function websiteCastingWidgetPublicCastingUrl (market: MarketPolymorphicId) {
  return `${window.domain_name || ''}/${market.market_type.toLowerCase()}s/${market.id}/castings/`
}

export function websiteCastingWidgetSigninUrl (market: MarketPolymorphicId) {
  return `${window.domain_name || ''}/users/${market.market_type.toLowerCase()}s/${market.id}/sign_in`
}

export function websiteCastingWidgetCreate (body: any) {
  return httpPost(`/${httpGetRoute()}/website_casting_widgets`, { website_casting_widget: body })
}

export function websiteCastingWidgetUpdate (body: any) {
  return httpPatch(`/${httpGetRoute()}/website_casting_widgets/${body.id}`, { website_casting_widget: body })
}

export function websiteCastingWidgetDelete (id: number) {
  return httpDelete(`/${httpGetRoute()}/website_casting_widgets/${id}`)
}

export function websiteCastingWidgetPluginDataPreview (body: any): Promise<WebsiteCastingWidgetShowResponse> {
  return httpPost(`/plugins/website_casting_widgets/show_preview`, { website_casting_widget: body })
}

export function websiteCastingWidgetPluginData (id: number): Promise<WebsiteCastingWidgetShowResponse> {
  return httpGet(`${window.domain_name}/plugins/website_casting_widgets/${id}`)
}

export function websiteCastingWidgetPluginOfferedService (market: MarketPolymorphicId, id: number, params: { token?: string, customer_id?: number } = {}): Promise<SuppliersCastingsGetServiceResponse> {
  return httpGet(`${websiteCastingWidgetPublicCastingUrl(market)}/get_service/${id}`, { ...params, hl: getHl() })
}

export function websiteCastingWidgetPluginComputePromotionalCode (market: MarketPolymorphicId, code: string, params: { service_id: number, casting_type: CastingType, zipcode: string, city: string, customer_id?: number }) {
  return httpGet(`${websiteCastingWidgetPublicCastingUrl(market)}/compute_promotional_code/${code}`, { ...params, hl: getHl() })
}

export function websiteCastingWidgetPluginRemoveAttachment (market: MarketPolymorphicId, uuid: string) {
  return httpDelete(`${websiteCastingWidgetPublicCastingUrl(market)}/attachment/${uuid}`, { hl: getHl() })
}

export function websiteCastingWidgetPluginUploadAttachment (market: MarketPolymorphicId, file: File) {
  const body = new FormData()
  body.append('attachment[file]', file)
  return httpPostMultipart(`${websiteCastingWidgetPublicCastingUrl(market)}/attachment?hl=${getHl()}`, body)
}

export function websiteCastingWidgetPluginGetServices (market: MarketPolymorphicId, params: any): Promise<SuppliersCastingsGetServicesResponse> {
  return httpGet(`${websiteCastingWidgetPublicCastingUrl(market)}/get_services`, { ...params, hl: getHl() })
}

export async function websiteCastingWidgetPluginLodgingValidateIcalUrl (market: MarketPolymorphicId, icalUrl: string): Promise<CastingValidateLodgingIcalUrlResponse> {
  return httpPost(`${websiteCastingWidgetPublicCastingUrl(market)}/validate_lodging_ical_url`, { ical_url: icalUrl })
}

export function websiteCastingWidgetPluginNewFromPluginPath (market: MarketPolymorphicId) {
  return `${websiteCastingWidgetPublicCastingUrl(market)}/new_from_plugin?hl=${getHl()}`
}

export function websiteCastingWidgetGetGroupSessionDates (market: MarketPolymorphicId, params: { offered_service_id: number, required_place: string, service_delivery_location_id: number, casting_type: CastingType }): Promise<string[]> {
  return httpGet(`${websiteCastingWidgetPublicCastingUrl(market)}/get_group_session_dates`, params)
}

export function websiteCastingWidgetGetGroupSessionsAtDay (market: MarketPolymorphicId, params: { day: string, offered_service_id: number, required_place: string, service_delivery_location_id: number, casting_type: CastingType }): Promise<SupplierCastingsGetGroupSessionsAtDay> {
  return httpGet(`${websiteCastingWidgetPublicCastingUrl(market)}/get_group_sessions_at_day`, params)
}

export function websiteCastingWidgetCity (market: MarketPolymorphicId, params: any) {
  return httpPost(`${websiteCastingWidgetPublicCastingUrl(market)}/city`, params)
}

export async function websiteCastingWidgetPluginComputeCastingPricesForEvents (market: MarketPolymorphicId, body: any, opts: { sync?: boolean, displayPriceWithTax: boolean }) {
  const rawApiResult: ComputedCastingPricesForEventsRaw = await (opts.sync ? httpPostSync : httpPost)(`${websiteCastingWidgetPublicCastingUrl(market)}/compute_casting_prices_for_events`, body)

  return rawApiResult
    .map(_ => ({
      ..._,
      first_reccurrence: prepareCastingPriceApiResult(_.first_reccurrence, opts.displayPriceWithTax),
      second_reccurrence: prepareCastingPriceApiResult(_.second_reccurrence, opts.displayPriceWithTax)
    }))
    .sort((a, b) => a.first_reccurrence.displayedPriceCents - b.second_reccurrence.displayedPriceCents)
}

export async function websiteCastingWidgetPluginComputeCastingTotalPrice (market: MarketPolymorphicId, body: any, opts: { sync?: boolean, displayPriceWithTax: boolean }) {
  const rawApiResult: ComputedCastingTotalPricesRaw = await (opts.sync ? httpPostSync : httpPost)(`${websiteCastingWidgetPublicCastingUrl(market)}/compute_casting_total_price`, body)

  return rawApiResult
    .map(supplierPrice => ({
      ...supplierPrice,
      displayedPriceCents: supplierPrice.total_price_cents + (opts.displayPriceWithTax ? supplierPrice.total_tax_cents : 0)
    }))
    .sort((a, b) => a.displayedPriceCents - b.displayedPriceCents)
}

export async function websiteCastingWidgetPluginRealtimeAvailabilities (market: MarketPolymorphicId, body: any, opts: { sync?: boolean }) {
  const rawApiResult: RealtimeAvailabilities = await (opts.sync ? httpPostSync : httpPost)(`${websiteCastingWidgetPublicCastingUrl(market)}/realtime_availabilities`, body)

  return rawApiResult.map(
    realtimeAvailabilitiesForOneAssignable => realtimeAvailabilitiesForOneAssignable.map(
      availability => moment.range(moment.parseZone(availability.start_time), moment.parseZone(availability.end_time))
    )
  )
}

export function websiteCastingWidgetPluginEmployeesMatchingDebug (market: MarketPolymorphicId, body: any): Promise<CastingEmployeesMatching> {
  return httpPost(`${websiteCastingWidgetPublicCastingUrl(market)}/employees_matching_debug`, body)
}

function getHl () {
  return window.locale.split('-')[0]
}

function prepareCastingPriceApiResult (pricedEvents: PricedEventRaw[], displayPriceWithTax: boolean) {
  const priceCents =  pricedEvents.reduce((acc, curr) => acc + +curr.price_cents, 0)
  const taxCents = pricedEvents.reduce((acc, curr) => acc + +curr.tax_cents, 0)
  const displayedPrice = priceCents + (displayPriceWithTax ? taxCents : 0)
  return { priced_events: pricedEvents, priceCents, taxCents, displayedPriceCents: displayedPrice }
}
